import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import { formatDay } from "../Issues/dates";
import Sparkline from "../common/Sparkline";
import { MAX_ADDED, LIMIT, refusalOf, matchesOf, suggestionsOf, wordsOf } from "./compare";
import { usePageFiles } from "./fixture";
import { usePushAdded } from "./usePushAdded";

const RISE_COLOR = "#16a34a";

/** A page's organic sessions over the days the main chart shows: the issue start dashed, the sessions rise solid green. */
const SessionsSpark = ({ page, days, shownDays, since, rise }) => {
    if (page === undefined) return <Loading />;
    if (!page?.sessions) return <NoSessions>no organic sessions</NoSessions>;
    const data = shownDays.map((day) => ({ day, v: page.sessions[days.indexOf(day)] }));
    if (!data.some(({ v }) => v > 0)) return <NoSessions>no organic sessions in these days</NoSessions>;
    return <Sparkline data={data} color="#818cf8" markers={since ? [since] : []} points={rise ? [{ day: rise, color: RISE_COLOR }] : []} fromZero scale />;
};

/** `path` cut into the stretches that are one of `words` and the stretches between, for highlighting. */
const stretchesOf = (path, words) => {
    const lower = path.toLowerCase();
    const hit = new Array(path.length).fill(false);
    for (const word of words) {
        for (let at = lower.indexOf(word); at >= 0; at = lower.indexOf(word, at + 1)) hit.fill(true, at, at + word.length);
    }
    return path.split("").reduce((stretches, letter, index) => {
        const last = stretches[stretches.length - 1];
        if (last && last.hit === hit[index]) last.text += letter;
        else stretches.push({ text: letter, hit: hit[index] });
        return stretches;
    }, []);
};

/** Typing settles for this long before the matching pages' files are fetched. */
const SETTLE_MS = 300;

/** `value`, once it has stopped changing for `ms`. */
const useSettled = (value, ms) => {
    const [settled, setSettled] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return settled;
};

/**
 * The pages that can be put on the chart beside this one: offered from where the traffic may have gone at
 * the chosen start, or found by searching the paths, each with its organic sessions. The drawer stays open
 * while pages are added; its footer lists what is on the chart.
 */
export default function CompareDrawer({ open, onClose, page, added, indexPages, compare, since, days, shownDays }) {
    const pushAdded = usePushAdded();
    const [text, setText] = useState("");
    const settledText = useSettled(text, SETTLE_MS);
    const ids = added.map(({ id }) => id);
    const full = ids.length >= MAX_ADDED;
    const suggestions = suggestionsOf(compare, page.id);
    const searching = text.trim() !== "";
    const matches = matchesOf(indexPages, text, page.id);
    // A match's file is fetched once the typing settles, and only for a page that has data to draw.
    const settledMatches = matchesOf(indexPages, settledText, page.id).filter(({ hasSessions }) => hasSessions);
    const pageFiles = usePageFiles(open ? [...new Set([...suggestions, ...settledMatches].map(({ id }) => id))] : []);
    const words = wordsOf(text);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (event) => event.key === "Escape" && onClose();
        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [open, onClose]);

    const add = (id) => {
        if (refusalOf(ids, id, page.id)) return;
        pushAdded([...ids, id]);
    };
    const remove = (id) => pushAdded(ids.filter((each) => each !== id));

    /** One page to offer: its path, why it is offered, its sessions, and adding or removing it. */
    const rowOf = ({ id, path, hasSessions = true }) => {
        const onChart = added.find((each) => each.id === id);
        const reasons = suggestions.find((each) => each.id === id)?.reasons || [];
        const rise = reasons.find(({ label }) => label === "Sessions rose")?.day;
        // A rise shows on the mini chart; only the canonical target and the redirect destination get a tag.
        const tags = reasons.filter(({ day }) => !day);
        return (
            <Row key={id} $color={onChart?.color}>
                <Who>
                    <Path title={path}>
                        {searching ? stretchesOf(path, words).map(({ text: stretch, hit }, index) => hit ? <Hit key={index}>{stretch}</Hit> : stretch) : path}
                    </Path>
                    {tags.length > 0 && <Reasons>{tags.map(({ label }) => <Reason key={label}>{label}</Reason>)}</Reasons>}
                </Who>
                <MiniChart>
                    {hasSessions
                        ? <SessionsSpark page={pageFiles.get(id)} days={days} shownDays={shownDays} since={since} rise={rise} />
                        : <NoSessions>no organic sessions</NoSessions>}
                </MiniChart>
                {onChart
                    ? (
                        <Tooltip title="Remove from chart" arrow placement="left">
                            <OnChart type="button" aria-label={`Remove ${path} from chart`} $color={onChart.color} onClick={() => remove(id)}>
                                <Swatch $color={onChart.color} />On chart<CloseIcon style={{ fontSize: "0.8rem" }} />
                            </OnChart>
                        </Tooltip>
                    )
                    : (
                        <Tooltip title={full ? LIMIT : "Add to chart"} arrow placement="left">
                            <Action>
                                <Add type="button" aria-label="Add to chart" disabled={full} onClick={() => add(id)}>
                                    <AddIcon style={{ fontSize: "1.05rem" }} />
                                </Add>
                            </Action>
                        </Tooltip>
                    )}
            </Row>
        );
    };

    return (
        <>
            <Backdrop $open={open} onClick={onClose} />
            <Panel $open={open} aria-hidden={!open}>
                <Header>
                    <Heading>
                        <Title>Add pages to the chart</Title>
                        <Subtitle title={page.path}>beside {page.path}{since && ` · issue ${formatDay(since)}`}</Subtitle>
                    </Heading>
                    <CloseBtn type="button" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></CloseBtn>
                </Header>
                <SearchBar>
                    <SearchIcon />
                    <Input type="search" value={text} onChange={(event) => setText(event.target.value)} placeholder="Search pages" aria-label="Find a page" />
                </SearchBar>

                <Body>
                    {searching
                        ? (
                            <>
                                <SectionHead>
                                    <SectionTitle>Matches</SectionTitle>
                                    <SectionNote>{matches.length > 0 ? `the ${matches.length} closest paths` : "every word has to be in the path, one typo allowed"}</SectionNote>
                                </SectionHead>
                                {matches.length > 0 ? <List>{matches.map(rowOf)}</List> : <Empty>No monitored page matches “{text.trim()}”.</Empty>}
                            </>
                        )
                        : (
                            <>
                                <SectionHead>
                                    <SectionTitle>Suggested</SectionTitle>
                                    <SectionNote>{since ? `traffic rose on or after ${formatDay(since)}` : "no issue start chosen"}</SectionNote>
                                </SectionHead>
                                {suggestions.length > 0 ? <List>{suggestions.map(rowOf)}</List> : <Empty>No page gained traffic around this issue. Search for one above.</Empty>}
                            </>
                        )}
                    {(searching ? matches : suggestions).length > 0 && (
                        <Key>
                            <span><KeyLine $color="#e0776f" $dashed />issue start</span>
                            <span><KeyLine $color={RISE_COLOR} />sessions rose</span>
                            <span>organic sessions, same days as the chart</span>
                        </Key>
                    )}
                </Body>

                <Footer>
                    <FooterPages>
                        <FooterLabel>On the chart</FooterLabel>
                        <Chip><Swatch $color="#475569" $solid />{page.path}</Chip>
                        {added.map(({ id, path, color }) => (
                            <Chip key={id} title={path}>
                                <Swatch $color={color} />{path}
                                <ChipRemove type="button" aria-label={`Remove ${path} from chart`} onClick={() => remove(id)}><CloseIcon style={{ fontSize: "0.75rem" }} /></ChipRemove>
                            </Chip>
                        ))}
                    </FooterPages>
                    <FooterEnd>
                        <Count>{ids.length} of {MAX_ADDED} added</Count>
                        <Done type="button" onClick={onClose}>Done</Done>
                    </FooterEnd>
                </Footer>
            </Panel>
        </>
    );
}

const Backdrop = styled.div`
    display: ${({ $open }) => $open ? "block" : "none"};
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.3);
    z-index: 1200;
`;

const Panel = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1201;
    display: flex;
    flex-direction: column;
    width: min(760px, 100vw);
    height: 100dvh;
    background: #f8fafc;
    border-left: 1px solid #e2e8f0;
    box-shadow: -8px 0 32px rgba(15, 23, 42, 0.14);
    transform: ${({ $open }) => $open ? "translateX(0)" : "translateX(100%)"};
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px 12px;
    background: #fff;
    flex-shrink: 0;
`;

const Heading = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.2px;
    color: #0f172a;
`;

const Subtitle = styled.div`
    margin-top: 3px;
    font-size: 0.72rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CloseBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    &:hover { background: #f1f5f9; color: #0f172a; }
`;

const SearchBar = styled.label`
    position: relative;
    display: block;
    margin: 0;
    padding: 0 20px 16px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    & > svg { position: absolute; left: 31px; top: 11px; font-size: 1.1rem; color: #94a3b8; pointer-events: none; }
`;

const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    height: 40px;
    padding: 0 12px 0 36px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    font-size: 0.82rem;
    color: #0f172a;
    transition: border-color 120ms, box-shadow 120ms, background 120ms;
    &:focus { outline: none; background: #fff; border-color: #a5b4fc; box-shadow: 0 0 0 3px #eef2ff; }
`;

const Body = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
`;

const SectionHead = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
`;

const SectionTitle = styled.span`
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #475569;
`;

const SectionNote = styled.span`
    font-size: 0.7rem;
    color: #94a3b8;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 240px 92px;
    align-items: center;
    gap: 14px;
    padding: 10px 12px 10px 14px;
    border: 1px solid ${({ $color }) => $color ? `${$color}55` : "#e8edf4"};
    border-left: 3px solid ${({ $color }) => $color || "transparent"};
    border-radius: 10px;
    background: #fff;
    transition: border-color 120ms, box-shadow 120ms;
    &:hover { border-color: ${({ $color }) => $color || "#c7d2fe"}; box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06); }
    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr) auto;
        & > :nth-child(2) { grid-column: 1 / -1; order: 1; }
    }
`;

const Who = styled.div`
    min-width: 0;
`;

const Path = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8rem;
    font-weight: 600;
    color: #0f172a;
`;

const Hit = styled.mark`
    padding: 0 1px;
    border-radius: 3px;
    background: #e0e7ff;
    color: #3730a3;
`;

const Reasons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 5px;
`;

const Reason = styled.span`
    padding: 1px 7px;
    border-radius: 999px;
    background: #eef2ff;
    font-size: 0.64rem;
    font-weight: 600;
    color: #4f46e5;
`;

const MiniChart = styled.div`
    height: 46px;
    min-width: 0;
    display: flex;
    align-items: center;
`;

const shimmer = keyframes`
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
`;

const Loading = styled.div`
    width: 100%;
    height: 30px;
    border-radius: 6px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.2s linear infinite;
`;

const NoSessions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 30px;
    border: 1px dashed #e2e8f0;
    border-radius: 6px;
    font-size: 0.66rem;
    color: #94a3b8;
`;

const Action = styled.span`
    justify-self: end;
`;

const Add = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid #c7d2fe;
    border-radius: 50%;
    background: #eef2ff;
    color: #4f46e5;
    cursor: pointer;
    transition: background 120ms, border-color 120ms, color 120ms, transform 120ms;
    &:hover { background: #4f46e5; border-color: #4f46e5; color: #fff; transform: scale(1.06); }
    &:disabled { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
`;

const OnChart = styled.button`
    justify-self: end;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 6px 4px 8px;
    border: 1px solid ${({ $color }) => `${$color}66`};
    border-radius: 999px;
    background: #fff;
    font-size: 0.66rem;
    font-weight: 700;
    color: #334155;
    white-space: nowrap;
    cursor: pointer;
    & > svg { color: #94a3b8; }
    &:hover { background: #f8fafc; & > svg { color: #0f172a; } }
`;

const Swatch = styled.span`
    flex-shrink: 0;
    width: 12px;
    height: 0;
    border-top: 2px ${({ $solid }) => $solid ? "solid" : "dashed"} ${({ $color }) => $color};
`;

const Empty = styled.div`
    padding: 28px 16px;
    border: 1px dashed #e2e8f0;
    border-radius: 10px;
    background: #fff;
    text-align: center;
    font-size: 0.76rem;
    color: #64748b;
`;

const Key = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 12px;
    font-size: 0.66rem;
    color: #94a3b8;
    span { display: inline-flex; align-items: center; gap: 5px; }
`;

const KeyLine = styled.span`
    width: 0;
    height: 11px;
    border-left: 2px ${({ $dashed }) => $dashed ? "dashed" : "solid"} ${({ $color }) => $color};
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 12px 20px;
    border-top: 1px solid #e2e8f0;
    background: #fff;
    flex-shrink: 0;
    @media (max-width: 600px) { flex-direction: column; align-items: stretch; }
`;

const FooterPages = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const FooterLabel = styled.span`
    margin-right: 2px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #94a3b8;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 220px;
    padding: 3px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ChipRemove = styled.button`
    display: inline-flex;
    flex-shrink: 0;
    margin-right: -4px;
    padding: 1px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    &:hover { background: #f1f5f9; color: #0f172a; }
`;

const FooterEnd = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    @media (max-width: 600px) { justify-content: space-between; }
`;

const Count = styled.span`
    font-size: 0.7rem;
    color: #64748b;
`;

const Done = styled.button`
    padding: 8px 18px;
    border: none;
    border-radius: 8px;
    background: #4f46e5;
    font-size: 0.76rem;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    &:hover { background: #4338ca; }
`;
